<template>
    <v-container class="mt-10 pb-12" style="min-width: 1200px; max-width: 1600px; padding-left: 24px; padding-right: 24px;">

        <!-- Header -->
        <div class="d-flex flex-wrap align-center mb-6">
            <div style="flex: 1 1 100%;" class="mb-4">
                <h1 class="text-h4 font-weight-bold mb-1">Time Tracking</h1>
                <p class="text-subtitle-1 grey--text mb-0">Desktop agent time data per team member</p>
            </div>
            <div class="d-flex flex-wrap align-center" style="gap: 8px; flex: 1 1 100%;">
                <v-autocomplete
                    v-model="selectedUserIds"
                    :items="userFilterItems"
                    label="Filter users"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    clearable
                    dense
                    outlined
                    hide-details
                    hide-selected
                    style="min-width: 260px; max-width: 340px;"
                    prepend-inner-icon="mdi-account-multiple-outline" />
                <v-menu v-model="timezoneMenu" :close-on-content-click="false" offset-y min-width="320">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn outlined v-bind="attrs" v-on="on" class="text-none">
                            <v-icon left small>mdi-earth</v-icon>
                            {{ timezone }}
                        </v-btn>
                    </template>
                    <v-card class="pa-4" min-width="320">
                        <div class="text-subtitle-2 font-weight-bold mb-2">Timezone</div>
                        <p class="text-caption grey--text mb-3">
                            Detected automatically from your browser. Daily breakdowns are
                            grouped using this timezone — useful for checking how a session
                            lands on the calendar from a team member's own local day.
                            Changing it here previews another timezone for this session
                            only — it isn't saved.
                        </p>
                        <v-autocomplete v-model="timezone" :items="timezoneItems" dense outlined
                            hide-details label="IANA timezone" class="mb-3" />
                        <v-btn v-if="timezone !== detectedTimezone" text small class="text-none mb-3 px-0"
                            @click="resetTimezone">
                            <v-icon left small>mdi-crosshairs-gps</v-icon>
                            Reset to detected ({{ detectedTimezone }})
                        </v-btn>
                        <v-btn color="primary" block class="text-none" @click="fetchData(); timezoneMenu = false">
                            Apply
                        </v-btn>
                    </v-card>
                </v-menu>
                <v-menu v-model="dateMenu" :close-on-content-click="false" offset-y min-width="300">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn outlined v-bind="attrs" v-on="on" class="text-none">
                            <v-icon left small>mdi-calendar-range</v-icon>
                            {{ dateRangeLabel }}
                        </v-btn>
                    </template>
                    <v-card class="pa-4" min-width="300">
                        <div class="text-subtitle-2 font-weight-bold mb-3">Quick ranges</div>
                        <div class="d-flex flex-wrap mb-3" style="gap: 6px;">
                            <v-chip v-for="preset in presets" :key="preset.label" small outlined
                                :color="activePreset === preset.label ? 'primary' : ''"
                                @click="applyPreset(preset)">
                                {{ preset.label }}
                            </v-chip>
                        </div>
                        <v-divider class="mb-3" />
                        <v-text-field v-model="dateFrom" label="From" type="date" dense outlined hide-details class="mb-2" />
                        <v-text-field v-model="dateTo" label="To" type="date" dense outlined hide-details class="mb-3" />
                        <v-btn color="primary" block class="text-none" @click="fetchData(); dateMenu = false">
                            Apply
                        </v-btn>
                    </v-card>
                </v-menu>
                <!-- Anti-cheat review queue: opens its own page, badge = pending count -->
                <SessionReviewsButton class="mr-2" />
                <v-btn outlined class="text-none text-no-wrap mr-2" :to="{ name: 'UnusualActivity' }">
                    <v-icon left small>mdi-alert-decagram-outline</v-icon>
                    Unusual activity
                </v-btn>
                <!-- Admin only: choose Time Tracking managers -->
                <TimeTrackingManagersButton />
                <v-spacer />
                <v-checkbox v-model="showLowActivityOnly" label="Show only low activity screenshots"
                    hide-details dense class="ma-0 pa-0" style="flex: none;" />
                <v-btn color="primary" outlined :loading="loading" @click="fetchData" class="text-none">
                    <v-icon left>mdi-refresh</v-icon>
                    Refresh
                </v-btn>
            </div>
        </div>

        <!-- AI analysis — separate section above the per-user cards -->
        <AiAnalysisSection
            :users="users"
            :date-from="dateFrom"
            :date-to="dateTo"
            @view-analysis="applyAnalysisView" />

        <!-- Active user filter (set when viewing a saved AI analysis) -->
        <div v-if="filterUserId" class="mb-4">
            <v-chip color="primary" outlined close @click:close="clearUserFilter">
                <v-icon left small>mdi-filter-outline</v-icon>
                Showing only {{ filterUserName }}
            </v-chip>
        </div>

        <!-- Loading / empty states -->
        <div v-if="loading" class="d-flex justify-center align-center" style="height: 300px;">
            <v-progress-circular indeterminate color="primary" size="48" />
        </div>

        <v-alert v-else-if="error" type="error" outlined class="mb-4">{{ error }}</v-alert>

        <div v-else-if="displayedUsers.length === 0" class="d-flex flex-column align-center justify-center grey--text"
            style="height: 300px; gap: 12px;">
            <v-icon size="72" color="grey lighten-2">mdi-clock-remove-outline</v-icon>
            <span class="text-h6">No tracking data for this period</span>
            <span class="text-body-2">
                {{ filterUserId
                    ? `${filterUserName} has no synced sessions in this range.`
                    : "The desktop agent hasn't synced any sessions yet." }}
            </span>
        </div>

        <!-- Per-user cards -->
        <div v-else>
            <v-card v-for="user in displayedUsers" :key="user.id" class="mb-4" elevation="1">
                <!-- User header row -->
                <div class="d-flex align-center pa-4" style="cursor: pointer; min-height: 128px;"
                    @click="toggleExpand(user.id)">
                    <v-avatar color="primary" size="44" class="mr-3">
                        <v-img v-if="user.avatar" :src="avatarUrl(user.avatar)" />
                        <span v-else class="white--text text-h6">{{ user.name[0] }}</span>
                    </v-avatar>

                    <div class="mr-6" style="min-width: 150px;">
                        <div class="font-weight-bold text-body-1">{{ user.name }}</div>
                        <div class="grey--text text-caption">
                            {{ user.days_active }} day{{ user.days_active !== 1 ? 's' : '' }} active
                            &nbsp;·&nbsp; {{ user.session_count }} session{{ user.session_count !== 1 ? 's' : '' }}
                        </div>
                    </div>

                    <DailyTimeBreakdown dense show-active class="flex-grow-1" style="min-width: 0;"
                        :total-seconds="user.total_seconds"
                        :idle-seconds="user.idle_seconds"
                        :days-active="user.days_active"
                        :daily="user.daily"
                        :date-from="dateFrom"
                        :date-to="dateTo" />

                    <v-icon class="ml-4">{{ expanded[user.id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </div>

                <!-- Expanded details -->
                <v-expand-transition>
                    <div v-if="expanded[user.id]">
                        <v-divider />
                        <v-row class="pa-4" no-gutters>

                            <!-- Input activity rates -->
                            <v-col cols="12" class="mb-3">
                                <div class="d-flex align-center" style="gap: 24px;">
                                    <div class="d-flex align-center" style="gap: 6px;">
                                        <v-icon small color="teal">mdi-keyboard-outline</v-icon>
                                        <span class="text-body-2">
                                            <span class="font-weight-bold teal--text">{{ keystrokesPerMin(user) }}</span>
                                            <span class="grey--text"> keystrokes/min avg</span>
                                        </span>
                                    </div>
                                    <div class="d-flex align-center" style="gap: 6px;">
                                        <v-icon small color="purple">mdi-cursor-default-click-outline</v-icon>
                                        <span class="text-body-2">
                                            <span class="font-weight-bold purple--text">{{ mouseClicksPerMin(user) }}</span>
                                            <span class="grey--text"> mouse clicks/min avg</span>
                                        </span>
                                    </div>
                                    <div class="d-flex align-center" style="gap: 6px;">
                                        <v-icon small color="indigo">mdi-mouse-outline</v-icon>
                                        <span class="text-body-2">
                                            <span class="font-weight-bold indigo--text">{{ mouseMovesPerMin(user) }}</span>
                                            <span class="grey--text"> mouse moves/min avg</span>
                                        </span>
                                    </div>
                                </div>
                            </v-col>

                            <!-- Daily breakdown -->
                            <v-col cols="12" md="4" class="pr-md-4 mb-4 mb-md-0">
                                <div class="text-caption text-uppercase font-weight-bold grey--text mb-2">
                                    Daily Breakdown
                                </div>
                                <div v-for="day in user.daily" :key="day.date" class="mb-3">
                                    <div class="d-flex" style="gap: 16px;">
                                        <!-- Time worked -->
                                        <div style="flex: 1 1 0; min-width: 0;">
                                            <div class="d-flex justify-space-between text-body-2 mb-1">
                                                <span>{{ formatDate(day.date) }}</span>
                                                <span class="font-weight-medium">{{ formatDuration(day.seconds) }}</span>
                                            </div>
                                            <v-progress-linear :value="dayBarPct(day.seconds, user)"
                                                color="primary" background-color="grey lighten-3" rounded height="5" />
                                        </div>
                                        <!-- Utilization: idle time vs total time tracked that day -->
                                        <div style="flex: 1 1 0; min-width: 0;">
                                            <div class="d-flex justify-space-between text-body-2 mb-1">
                                                <span class="grey--text">Utilization</span>
                                                <span class="font-weight-medium" :class="utilizationTextClass(dayUtilizationPct(day))">{{ dayUtilizationPct(day) }}%</span>
                                            </div>
                                            <v-progress-linear :value="dayUtilizationPct(day)"
                                                :color="utilizationColor(dayUtilizationPct(day))" background-color="grey lighten-3" rounded height="5" />
                                        </div>
                                    </div>
                                </div>
                                <div v-if="user.daily.length === 0" class="grey--text text-caption">No daily data</div>
                            </v-col>

                            <!-- Top apps -->
                            <v-col cols="12" md="2" class="pr-md-4 mb-4 mb-md-0">
                                <div class="text-caption text-uppercase font-weight-bold grey--text mb-2">
                                    Top Apps
                                </div>
                                <div v-for="(app, idx) in user.top_apps.slice(0, 7)" :key="app.app_name"
                                    class="d-flex align-center mb-1">
                                    <span class="grey--text text-caption mr-2" style="min-width: 16px;">{{ idx + 1 }}</span>
                                    <span class="text-body-2 flex-grow-1 text-truncate" :title="app.app_name">
                                        {{ cleanAppName(app.app_name) }}
                                    </span>
                                    <span class="text-caption font-weight-medium ml-2">{{ formatDuration(app.seconds) }}</span>
                                </div>
                                <div v-if="user.top_apps.length === 0" class="grey--text text-caption">No app data</div>
                            </v-col>

                            <!-- Top sites -->
                            <v-col cols="12" md="2" class="pr-md-4 mb-4 mb-md-0">
                                <div class="text-caption text-uppercase font-weight-bold grey--text mb-2">
                                    Top Sites
                                </div>
                                <div v-for="(site, idx) in (user.top_sites || []).slice(0, 7)" :key="site.domain"
                                    class="d-flex align-center mb-1">
                                    <span class="grey--text text-caption mr-2" style="min-width: 16px;">{{ idx + 1 }}</span>
                                    <v-avatar size="16" class="mr-2" tile>
                                        <img :src="faviconUrl(site.domain)" :alt="site.domain" @error="onFaviconError" />
                                    </v-avatar>
                                    <span class="text-body-2 flex-grow-1 text-truncate" :title="site.domain">
                                        {{ site.domain }}
                                    </span>
                                    <span class="text-caption font-weight-medium ml-2">{{ formatDuration(site.seconds) }}</span>
                                </div>
                                <div v-if="!(user.top_sites || []).length" class="grey--text text-caption">No site data</div>
                            </v-col>

                            <!-- Tasks -->
                            <v-col cols="12" md="4">
                                <div class="text-caption text-uppercase font-weight-bold grey--text mb-2">
                                    Tasks Worked On
                                </div>
                                <div v-for="task in user.tasks.slice(0, 7)" :key="task.id"
                                    class="d-flex align-center mb-1">
                                    <v-icon x-small class="mr-1 grey--text">mdi-checkbox-marked-circle-outline</v-icon>
                                    <span class="text-body-2 flex-grow-1 text-truncate" :title="task.name">
                                        {{ task.name }}
                                    </span>
                                    <span class="text-caption font-weight-medium ml-2">{{ formatDuration(task.seconds) }}</span>
                                </div>
                                <div v-if="user.tasks.length === 0" class="grey--text text-caption">No task data</div>
                            </v-col>

                        </v-row>

                        <!-- Screenshots strip -->
                        <v-divider />
                        <div class="pa-4">
                            <div class="d-flex align-center mb-3">
                                <v-icon small class="mr-2 grey--text">mdi-monitor-screenshot</v-icon>
                                <span class="text-caption text-uppercase font-weight-bold grey--text">
                                    Screenshots<template v-if="screenshotsMeta[user.id]"> ({{ screenshotsMeta[user.id].total }} total, {{ screenshotsMeta[user.id].lowActivity }} low activity)</template>
                                </span>
                                <v-progress-circular v-if="screenshotsLoading[user.id] || screenshotsMetaLoading[user.id]" indeterminate size="14" width="2"
                                    color="primary" class="ml-2" />
                            </div>

                            <!-- No screenshots yet -->
                            <div v-if="!screenshotsMetaLoading[user.id] && screenshotsMeta[user.id] && screenshotsMeta[user.id].total === 0"
                                class="grey--text text-caption">
                                No screenshots captured in this period.
                            </div>

                            <!-- Screenshots exist, but none match the active filter (among those loaded) -->
                            <div v-else-if="!screenshotsLoading[user.id] && !screenshotsMetaLoading[user.id] && screenshotsMeta[user.id] && screenshotsMeta[user.id].total > 0 && filteredScreenshots(user.id).length === 0"
                                class="grey--text text-caption">
                                No low activity screenshots found in this period.
                            </div>

                            <!-- Thumbnail strips, grouped by day when more than one day is in range -->
                            <div v-else-if="filteredScreenshots(user.id).length > 0">
                                <div v-for="group in screenshotGroups(user.id)" :key="group.date" class="mb-4">
                                    <div v-if="isMultiDayRange" class="text-caption font-weight-bold grey--text text--darken-1 mb-2">
                                        {{ formatDate(group.date) }}
                                    </div>
                                    <div class="screenshot-strip d-flex"
                                        style="overflow-x: auto; gap: 10px; padding-bottom: 6px;">
                                        <div v-for="shot in group.shots" :key="shot.id"
                                            class="screenshot-thumb flex-shrink-0"
                                            style="cursor: pointer; width: 160px;"
                                            @click="openViewer(shot, user.id)">
                                            <div style="position: relative;">
                                                <!-- Skeleton while image loads -->
                                                <div v-if="!thumbnailUrls[shot.id]"
                                                    class="grey lighten-2 d-flex align-center justify-center"
                                                    style="width: 160px; height: 90px; border-radius: 4px;">
                                                    <v-progress-circular v-if="thumbnailLoading[shot.id]"
                                                        indeterminate size="20" width="2" color="grey" />
                                                    <v-icon v-else color="grey lighten-1">mdi-image-outline</v-icon>
                                                </div>
                                                <!-- Thumbnail image -->
                                                <v-img v-else
                                                    :src="thumbnailUrls[shot.id]"
                                                    width="160"
                                                    height="90"
                                                    style="border-radius: 4px; border: 2px solid transparent;"
                                                    class="screenshot-thumb-img"
                                                    cover />
                                                <!-- Timestamp overlay -->
                                                <div class="screenshot-time-overlay text-caption white--text"
                                                    style="position: absolute; bottom: 0; left: 0; right: 0;
                                                           background: rgba(0,0,0,0.55); border-radius: 0 0 4px 4px;
                                                           padding: 2px 5px; font-size: 10px;">
                                                    {{ formatShotTime(shot.captured_at) }}
                                                </div>
                                                <!-- Low activity warning badge -->
                                                <v-tooltip v-if="isLowActivity(shot)" top>
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-icon v-bind="attrs" v-on="on" color="orange darken-2" small
                                                            style="position: absolute; top: 4px; right: 4px; background: white;
                                                                   border-radius: 50%; padding: 2px;">
                                                            mdi-alert
                                                        </v-icon>
                                                    </template>
                                                    <span>Low activity (idle ≥60% of the 5 minutes before capture)</span>
                                                </v-tooltip>
                                                <!-- Delete — only the owner of these screenshots can delete them -->
                                                <v-tooltip v-if="isOwnScreenshots(user.id)" top>
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-btn v-bind="attrs" v-on="on" icon x-small
                                                            style="position: absolute; top: 4px; left: 4px; background: white;"
                                                            @click.stop="askDeleteScreenshot(shot, user.id)">
                                                            <v-icon x-small color="red darken-2">mdi-delete-outline</v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Delete screenshot</span>
                                                </v-tooltip>
                                            </div>

                                            <!-- Activity bars: keystrokes/min + mouse moves/min + idle %
                                                 for the 5 minutes leading up to this screenshot -->
                                            <div v-if="shot.idle_pct !== null" class="mt-1">
                                                <template v-if="shot.keystrokes_per_min !== null">
                                                    <div class="d-flex align-center" style="gap: 4px;">
                                                        <v-icon style="font-size: 12px;" color="teal">mdi-keyboard-outline</v-icon>
                                                        <v-progress-linear :value="activityBarPct(shot.keystrokes_per_min, 60)"
                                                            color="teal" background-color="grey lighten-3" rounded height="4" />
                                                        <span class="grey--text text--darken-1" style="font-size: 10px; min-width: 24px; text-align: right;">
                                                            {{ shot.keystrokes_per_min }}
                                                        </span>
                                                    </div>
                                                    <div class="d-flex align-center mt-1" style="gap: 4px;">
                                                        <v-icon style="font-size: 12px;" color="purple">mdi-cursor-default-click-outline</v-icon>
                                                        <v-progress-linear :value="activityBarPct(shot.mouse_clicks_per_min, 60)"
                                                            color="purple" background-color="grey lighten-3" rounded height="4" />
                                                        <span class="grey--text text--darken-1" style="font-size: 10px; min-width: 24px; text-align: right;">
                                                            {{ shot.mouse_clicks_per_min }}
                                                        </span>
                                                    </div>
                                                    <div class="d-flex align-center mt-1" style="gap: 4px;">
                                                        <v-icon style="font-size: 12px;" color="indigo">mdi-mouse-outline</v-icon>
                                                        <v-progress-linear :value="activityBarPct(shot.mouse_events_per_min, 120)"
                                                            color="indigo" background-color="grey lighten-3" rounded height="4" />
                                                        <span class="grey--text text--darken-1" style="font-size: 10px; min-width: 24px; text-align: right;">
                                                            {{ shot.mouse_events_per_min }}
                                                        </span>
                                                    </div>
                                                </template>
                                                <div class="d-flex align-center mt-1" style="gap: 4px;">
                                                    <v-icon style="font-size: 12px;" color="orange darken-2">mdi-timer-sand</v-icon>
                                                    <v-progress-linear :value="shot.idle_pct"
                                                        color="orange darken-2" background-color="grey lighten-3" rounded height="4" />
                                                    <span class="grey--text text--darken-1" style="font-size: 10px; min-width: 24px; text-align: right;">
                                                        {{ shot.idle_pct }}%
                                                    </span>
                                                </div>
                                            </div>
                                            <div v-else class="text-center grey--text mt-1" style="font-size: 10px;">
                                                no activity data
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Load all — only the most recent screenshotsInitialLimit are fetched by default -->
                            <div v-if="showLoadAllButton(user.id)" class="mt-2">
                                <v-btn text small color="primary" class="text-none"
                                    :loading="screenshotsLoadingAll[user.id]" @click="loadAllScreenshots(user.id)">
                                    <v-icon left small>mdi-tray-arrow-down</v-icon>
                                    Load all {{ screenshotsMeta[user.id].total }} screenshots
                                </v-btn>
                            </div>
                        </div>
                    </div>
                </v-expand-transition>
            </v-card>
        </div>

        <!-- Screenshot viewer dialog -->
        <v-dialog v-model="viewerOpen" max-width="1200" @keydown.esc="viewerOpen = false">
            <v-card v-if="viewerShot">
                <!-- Toolbar -->
                <v-toolbar dense flat class="pr-2">
                    <v-icon class="mr-2">mdi-monitor-screenshot</v-icon>
                    <v-toolbar-title class="text-body-1 font-weight-medium">
                        {{ formatShotDateTime(viewerShot.captured_at) }}
                    </v-toolbar-title>
                    <v-chip v-if="isLowActivity(viewerShot)" x-small color="orange darken-2" outlined class="ml-2">
                        <v-icon left x-small>mdi-alert</v-icon>
                        Low activity
                    </v-chip>
                    <v-spacer />
                    <!-- Navigate between screenshots -->
                    <v-btn icon :disabled="viewerIndex <= 0" @click="navigateViewer(-1)">
                        <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                    <span class="text-caption grey--text mx-1">
                        {{ viewerIndex + 1 }} / {{ viewerList.length }}
                    </span>
                    <v-btn icon :disabled="viewerIndex >= viewerList.length - 1" @click="navigateViewer(1)">
                        <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                    <v-btn v-if="isOwnScreenshots(viewerUserId)" icon color="red darken-2"
                        @click="askDeleteScreenshot(viewerShot, viewerUserId)">
                        <v-icon>mdi-delete-outline</v-icon>
                    </v-btn>
                    <v-btn icon @click="viewerOpen = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-divider />

                <!-- Activity summary: 5 minutes leading up to this screenshot -->
                <div class="d-flex align-center px-4 py-3" style="gap: 32px;">
                    <template v-if="viewerShot.idle_pct !== null">
                        <template v-if="viewerShot.keystrokes_per_min !== null">
                            <div class="d-flex align-center flex-grow-1" style="gap: 8px;">
                                <v-icon small color="teal">mdi-keyboard-outline</v-icon>
                                <span class="text-caption grey--text" style="white-space: nowrap;">Keystrokes/min</span>
                                <v-progress-linear :value="activityBarPct(viewerShot.keystrokes_per_min, 60)"
                                    color="teal" background-color="grey lighten-3" rounded height="8" />
                                <span class="text-body-2 font-weight-bold" style="min-width: 28px; text-align: right;">
                                    {{ viewerShot.keystrokes_per_min }}
                                </span>
                            </div>
                            <div class="d-flex align-center flex-grow-1" style="gap: 8px;">
                                <v-icon small color="purple">mdi-cursor-default-click-outline</v-icon>
                                <span class="text-caption grey--text" style="white-space: nowrap;">Clicks/min</span>
                                <v-progress-linear :value="activityBarPct(viewerShot.mouse_clicks_per_min, 60)"
                                    color="purple" background-color="grey lighten-3" rounded height="8" />
                                <span class="text-body-2 font-weight-bold" style="min-width: 28px; text-align: right;">
                                    {{ viewerShot.mouse_clicks_per_min }}
                                </span>
                            </div>
                            <div class="d-flex align-center flex-grow-1" style="gap: 8px;">
                                <v-icon small color="indigo">mdi-mouse-outline</v-icon>
                                <span class="text-caption grey--text" style="white-space: nowrap;">Mouse/min</span>
                                <v-progress-linear :value="activityBarPct(viewerShot.mouse_events_per_min, 120)"
                                    color="indigo" background-color="grey lighten-3" rounded height="8" />
                                <span class="text-body-2 font-weight-bold" style="min-width: 28px; text-align: right;">
                                    {{ viewerShot.mouse_events_per_min }}
                                </span>
                            </div>
                        </template>
                        <div class="d-flex align-center flex-grow-1" style="gap: 8px;">
                            <v-icon small color="orange darken-2">mdi-timer-sand</v-icon>
                            <span class="text-caption grey--text" style="white-space: nowrap;">Idle %</span>
                            <v-progress-linear :value="viewerShot.idle_pct"
                                color="orange darken-2" background-color="grey lighten-3" rounded height="8" />
                            <span class="text-body-2 font-weight-bold" style="min-width: 28px; text-align: right;">
                                {{ viewerShot.idle_pct }}%
                            </span>
                        </div>
                    </template>
                    <span v-else class="text-caption grey--text">
                        No tracked activity in the 5 minutes before this screenshot.
                    </span>
                </div>
                <v-divider />

                <!-- Image -->
                <div class="d-flex align-center justify-center pa-4"
                    style="background: #1a1a1a; min-height: 400px; max-height: 75vh; overflow: auto;">
                    <div v-if="viewerLoading" class="d-flex flex-column align-center" style="gap: 12px;">
                        <v-progress-circular indeterminate color="primary" size="40" />
                        <span class="grey--text text-caption">Loading screenshot…</span>
                    </div>
                    <img v-else-if="thumbnailUrls[viewerShot.id]"
                        :src="thumbnailUrls[viewerShot.id]"
                        style="max-width: 100%; max-height: 72vh; object-fit: contain; border-radius: 4px;" />
                    <div v-else class="grey--text text-caption">Failed to load image.</div>
                </div>
            </v-card>
        </v-dialog>

        <!-- Delete confirmation -->
        <v-dialog v-model="confirmDeleteDialogOpen" max-width="400">
            <v-card v-if="confirmDeleteShot">
                <v-card-title class="text-h6">Delete screenshot?</v-card-title>
                <v-card-text>
                    This will permanently delete the screenshot captured at
                    {{ formatShotDateTime(confirmDeleteShot.captured_at) }}. This cannot be undone.
                    <div v-if="deletionPreview && deletionPreview.loading" class="d-flex align-center mt-3" style="gap: 8px;">
                        <v-progress-circular indeterminate size="16" width="2" color="grey" />
                        <span class="grey--text text-caption">Checking tracked time…</span>
                    </div>
                    <v-alert v-else-if="deletionPreview && deletionPreview.sessionActive"
                        type="info" dense outlined class="mt-3 mb-0 text-body-2">
                        Your timer is still running for this period, so no tracked time will be removed — only the screenshot.
                    </v-alert>
                    <v-alert v-else-if="deletionPreview && deletionPreview.secondsDeleted > 0"
                        type="warning" dense outlined class="mt-3 mb-0 text-body-2">
                        Deleting this screenshot will also delete
                        <strong>{{ formatDuration(deletionPreview.secondsDeleted) }}</strong> of your tracked time.
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="cancelDeleteScreenshot">Cancel</v-btn>
                    <v-btn color="error" text :loading="isDeleting" @click="confirmDeleteScreenshot">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-container>
</template>

<script>
import DailyTimeBreakdown from './DailyTimeBreakdown.vue';
import AiAnalysisSection from './AiAnalysisSection.vue';
import SessionReviewsButton from './SessionReviewsButton.vue';
import TimeTrackingManagersButton from './TimeTrackingManagersButton.vue';

export default {
    name: 'AgentTimeTrackingComponent',

    components: { DailyTimeBreakdown, AiAnalysisSection, SessionReviewsButton, TimeTrackingManagersButton },

    data() {
        const today = new Date();
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay() + 1); // Monday

        return {
            loading: false,
            error: null,
            dateMenu: false,
            timezoneMenu: false,
            detectedTimezone: this.$date.tz.guess(),
            timezone: this.$date.tz.guess(),
            activePreset: 'This week',
            dateFrom: weekStart.toISOString().slice(0, 10),
            dateTo: today.toISOString().slice(0, 10),
            users: [],
            expanded: {},

            // Multi-select dropdown — when non-empty, only these users' cards are shown.
            selectedUserIds: [],

            // When set (from viewing a saved AI analysis), only this user's
            // card is shown until the filter chip is cleared.
            filterUserId: null,
            filterUserName: null,
            presets: [
                { label: 'Today', days: 0 },
                { label: 'Yesterday', days: -1, offset: 1 },
                { label: 'This week', thisWeek: true },
                { label: 'Last 7 days', days: 6 },
                { label: 'Last 14 days', days: 13 },
                { label: 'This month', thisMonth: true },
                { label: 'Last 30 days', days: 29 },
            ],

            // Screenshots — keyed by userId
            showLowActivityOnly: false,
            screenshotsInitialLimit: 30, // most recent N loaded automatically; "Load all" fetches the rest
            screenshotsData: {},    // userId -> [{ id, captured_at, keystrokes_per_min, mouse_events_per_min, idle_pct }]
            screenshotsLoading: {}, // userId -> bool
            screenshotsMeta: {},        // userId -> { total, lowActivity } — true counts for the full date range, from the cheap counts endpoint
            screenshotsMetaLoading: {}, // userId -> bool
            screenshotsAllLoaded: {},   // userId -> bool, true once "Load all" has fetched the full range
            screenshotsLoadingAll: {},  // userId -> bool, loading state for the "Load all" button
            thumbnailUrls: {},      // screenshotId -> base64 data URI
            thumbnailLoading: {},   // screenshotId -> bool

            // Viewer dialog
            viewerOpen: false,
            viewerShot: null,    // { id, captured_at }
            viewerList: [],      // the user's screenshot list for prev/next
            viewerUserId: null,  // owner of viewerList, for the delete permission check
            viewerLoading: false,

            // Delete confirmation
            confirmDeleteShot: null,   // shot pending confirmation, or null
            confirmDeleteUserId: null,
            isDeleting: false,
            deletionPreview: null,      // { loading, secondsDeleted, sessionActive } for confirmDeleteShot
        };
    },

    computed: {
        // Full IANA identifier list where the browser supports it (Chrome/Edge
        // 99+, Firefox 122+); falls back to just the detected zone otherwise
        // so the autocomplete still has at least one valid entry.
        timezoneItems() {
            try {
                return Intl.supportedValuesOf('timeZone');
            } catch (_) {
                return [this.detectedTimezone];
            }
        },
        displayedUsers() {
            let list = this.users;
            if (this.filterUserId) {
                list = list.filter(u => u.id === this.filterUserId);
            }
            if (this.selectedUserIds.length) {
                list = list.filter(u => this.selectedUserIds.includes(u.id));
            }
            return [...list].sort((a, b) => a.name.localeCompare(b.name));
        },
        userFilterItems() {
            return [...this.users]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(u => ({ text: u.name, value: u.id }));
        },
        dateRangeLabel() {
            if (this.dateFrom === this.dateTo) return this.formatDate(this.dateFrom);
            return `${this.formatDate(this.dateFrom)} – ${this.formatDate(this.dateTo)}`;
        },
        isMultiDayRange() {
            return this.dateFrom !== this.dateTo;
        },
        viewerIndex() {
            if (!this.viewerShot || !this.viewerList.length) return -1;
            return this.viewerList.findIndex(s => s.id === this.viewerShot.id);
        },
        currentUserId() {
            return this.$store.getters['auth/get_user']?.id ?? null;
        },
        confirmDeleteDialogOpen: {
            get() { return this.confirmDeleteShot !== null; },
            set(val) { if (!val) this.cancelDeleteScreenshot(); },
        },
    },

    created() {
        // Non-reactive promise cache — prevents duplicate in-flight requests
        // when the viewer opens while a background thumbnail load is already running.
        this._thumbnailPromises = {};
    },

    mounted() {
        this.fetchData();
    },

    methods: {
        async fetchData() {
            this.loading = true;
            this.error = null;
            // Clear screenshots when date range changes.
            this.screenshotsData = {};
            this.screenshotsLoading = {};
            this.screenshotsMeta = {};
            this.screenshotsMetaLoading = {};
            this.screenshotsAllLoaded = {};
            this.screenshotsLoadingAll = {};

            let payload = {
                date_from: this.dateFrom,
                date_to: this.dateTo,
                timezone: this.timezone,
            }
            try {
                const res = await this.$axios.get('api/agent/dashboard', payload);
                if (res.data?.response) {
                    this.users = res.data.users;
                    // Re-load screenshots for any cards that are still expanded.
                    for (const userId of Object.keys(this.expanded)) {
                        if (this.expanded[userId]) {
                            this.loadScreenshots(userId);
                            this.loadScreenshotCounts(userId);
                        }
                    }
                } else {
                    this.error = 'Failed to load tracking data.';
                }
            } catch (e) {
                this.error = e?.response?.data?.message || 'Failed to load tracking data.';
            } finally {
                this.loading = false;
            }
        },

        resetTimezone() {
            this.timezone = this.detectedTimezone;
        },

        applyPreset(preset) {
            const today = new Date();
            const pad = d => d.toISOString().slice(0, 10);
            this.activePreset = preset.label;

            if (preset.thisWeek) {
                const mon = new Date(today);
                mon.setDate(today.getDate() - ((today.getDay() + 6) % 7));
                this.dateFrom = pad(mon);
                this.dateTo = pad(today);
            } else if (preset.thisMonth) {
                this.dateFrom = pad(new Date(today.getFullYear(), today.getMonth(), 1));
                this.dateTo = pad(today);
            } else if (preset.offset) {
                const d = new Date(today);
                d.setDate(d.getDate() - preset.offset);
                this.dateFrom = pad(d);
                this.dateTo = pad(d);
            } else {
                const from = new Date(today);
                from.setDate(today.getDate() - preset.days);
                this.dateFrom = pad(from);
                this.dateTo = pad(today);
            }
            this.fetchData();
            this.dateMenu = false;
        },

        // ---- AI analysis integration ----------------------------------------

        // Called when a saved AI analysis is opened: jump the dashboard to
        // that analysis's date range and show only the analysed user's card.
        applyAnalysisView({ user_id, user_name, date_from, date_to }) {
            this.dateFrom = date_from;
            this.dateTo = date_to;
            this.activePreset = null;
            this.filterUserId = user_id;
            this.filterUserName = user_name;
            this.fetchData();
        },

        clearUserFilter() {
            this.filterUserId = null;
            this.filterUserName = null;
        },

        toggleExpand(userId) {
            this.$set(this.expanded, userId, !this.expanded[userId]);
            if (this.expanded[userId] && !this.screenshotsData[userId]) {
                this.loadScreenshots(userId);
                this.loadScreenshotCounts(userId);
            }
        },

        // ---- Screenshots ----------------------------------------------------

        screenshotCount(userId) {
            return this.screenshotsData[userId]?.length ?? 0;
        },

        // Low activity = idle for at least 60% of the 5-minute window before
        // the screenshot, based on real idle_seconds tracked by the agent
        // (GetLastInputInfo), not keystroke/click counts. Screenshots with no
        // tracked session data (null idle_pct) are excluded — that's a
        // distinct "no data" state, not a low-activity one.
        isLowActivity(shot) {
            return shot.idle_pct !== null && shot.idle_pct >= 60;
        },

        filteredScreenshots(userId) {
            const list = this.screenshotsData[userId] ?? [];
            if (!this.showLowActivityOnly) return list;
            return list.filter(shot => this.isLowActivity(shot));
        },

        // Groups a user's filteredScreenshots by local calendar day, preserving
        // the backend's captured_at-desc ordering within and across groups.
        screenshotGroups(userId) {
            const groups = [];
            const byDate = new Map();
            for (const shot of this.filteredScreenshots(userId)) {
                const date = this.localDateKey(shot.captured_at);
                if (!byDate.has(date)) {
                    const group = { date, shots: [] };
                    byDate.set(date, group);
                    groups.push(group);
                }
                byDate.get(date).shots.push(shot);
            }
            return groups;
        },

        // Loads the most recent `screenshotsInitialLimit` screenshots, unless
        // "Load all" has already been triggered for this user (see
        // loadAllScreenshots), in which case the full date range is fetched.
        async loadScreenshots(userId) {
            this.$set(this.screenshotsLoading, userId, true);
            let payload = {
                date_from: this.dateFrom,
                date_to: this.dateTo,
                timezone: this.timezone,
            }
            if (!this.screenshotsAllLoaded[userId]) {
                payload.limit = this.screenshotsInitialLimit;
            }
            try {
                const res = await this.$axios.get(`api/agent/users/${userId}/screenshots`, payload);
                if (res.data?.response) {
                    this.$set(this.screenshotsData, userId, res.data.screenshots);
                    // Kick off thumbnail loads in the background, 3 at a time.
                    this.loadThumbnailsForUser(userId);
                }
            } catch (_) {
                this.$set(this.screenshotsData, userId, []);
            } finally {
                this.$set(this.screenshotsLoading, userId, false);
            }
        },

        // Cheap counts (total + low activity) for the full date range —
        // independent of the capped list above, so the header always shows
        // the true totals even before "Load all" is clicked.
        async loadScreenshotCounts(userId) {
            this.$set(this.screenshotsMetaLoading, userId, true);
            let payload = {
                date_from: this.dateFrom,
                date_to: this.dateTo,
                timezone: this.timezone,
            }
            try {
                const res = await this.$axios.get(`api/agent/users/${userId}/screenshots/counts`, payload);
                if (res.data?.response) {
                    this.$set(this.screenshotsMeta, userId, {
                        total: res.data.total,
                        lowActivity: res.data.low_activity,
                    });
                }
            } catch (_) {
                this.$set(this.screenshotsMeta, userId, { total: 0, lowActivity: 0 });
            } finally {
                this.$set(this.screenshotsMetaLoading, userId, false);
            }
        },

        async loadAllScreenshots(userId) {
            this.$set(this.screenshotsLoadingAll, userId, true);
            this.$set(this.screenshotsAllLoaded, userId, true);
            await this.loadScreenshots(userId);
            this.$set(this.screenshotsLoadingAll, userId, false);
        },

        // Whether to show the "Load all" button — only once we know the true
        // total (screenshotsMeta loaded) and there's more to fetch than what's
        // currently loaded.
        showLoadAllButton(userId) {
            const meta = this.screenshotsMeta[userId];
            if (!meta || this.screenshotsAllLoaded[userId]) return false;
            return this.screenshotCount(userId) < meta.total;
        },

        async loadThumbnailsForUser(userId) {
            const ids = (this.screenshotsData[userId] ?? []).map(shot => shot.id);
            await this.loadThumbnailsBatch(ids);
        },

        // Fetches all not-yet-loaded ids in a single request instead of one
        // request per screenshot — with hundreds of screenshots the per-request
        // overhead was the bottleneck, not the image bytes themselves.
        loadThumbnailsBatch(ids) {
            const pending = ids.filter(id => !this.thumbnailUrls[id] && !this._thumbnailPromises[id]);
            if (!pending.length) return Promise.resolve();

            for (const id of pending) this.$set(this.thumbnailLoading, id, true);

            const promise = this.$axios
                .post('api/agent/screenshots/batch', { ids: pending })
                .then(res => {
                    if (res.data?.response) {
                        for (const [id, dataUri] of Object.entries(res.data.screenshots)) {
                            this.$set(this.thumbnailUrls, id, dataUri);
                        }
                    }
                })
                .finally(() => {
                    for (const id of pending) {
                        this.$set(this.thumbnailLoading, id, false);
                        delete this._thumbnailPromises[id];
                    }
                });

            for (const id of pending) this._thumbnailPromises[id] = promise;
            return promise;
        },

        // Fallback for a single screenshot not already covered by a batch load
        // (e.g. opening the viewer before loadThumbnailsForUser() has finished).
        loadThumbnail(id) {
            if (this.thumbnailUrls[id]) return Promise.resolve();
            if (this._thumbnailPromises[id]) return this._thumbnailPromises[id];
            return this.loadThumbnailsBatch([id]);
        },

        // ---- Viewer ---------------------------------------------------------

        async openViewer(shot, userId) {
            this.viewerShot = shot;
            this.viewerUserId = userId;
            // Navigation should walk the same filtered set the user is browsing.
            this.viewerList = this.filteredScreenshots(userId);
            this.viewerOpen = true;
            if (!this.thumbnailUrls[shot.id]) {
                this.viewerLoading = true;
                await this.loadThumbnail(shot.id);
                this.viewerLoading = false;
            }
        },

        async navigateViewer(delta) {
            const next = this.viewerList[this.viewerIndex + delta];
            if (!next) return;
            this.viewerShot = next;
            if (!this.thumbnailUrls[next.id]) {
                this.viewerLoading = true;
                await this.loadThumbnail(next.id);
                this.viewerLoading = false;
            }
        },

        // ---- Delete -----------------------------------------------------------

        // Own screenshots only — deletion is self-service even for admins
        // viewing another user's dashboard card.
        isOwnScreenshots(userId) {
            return this.currentUserId !== null && this.currentUserId === userId;
        },

        async askDeleteScreenshot(shot, userId) {
            this.confirmDeleteShot = shot;
            this.confirmDeleteUserId = userId;
            this.deletionPreview = { loading: true, secondsDeleted: 0, sessionActive: false };
            try {
                const res = await this.$axios.get(`api/agent/screenshots/${shot.id}/deletion-preview`);
                if (res.data?.response && this.confirmDeleteShot?.id === shot.id) {
                    this.deletionPreview = {
                        loading: false,
                        secondsDeleted: res.data.seconds_deleted ?? 0,
                        sessionActive: !!res.data.session_active,
                    };
                }
            } catch (_) {
                if (this.confirmDeleteShot?.id === shot.id) {
                    this.deletionPreview = { loading: false, secondsDeleted: 0, sessionActive: false };
                }
            }
        },

        cancelDeleteScreenshot() {
            this.confirmDeleteShot = null;
            this.confirmDeleteUserId = null;
            this.deletionPreview = null;
        },

        async confirmDeleteScreenshot() {
            const shot = this.confirmDeleteShot;
            const userId = this.confirmDeleteUserId;
            if (!shot) return;

            this.isDeleting = true;
            try {
                const res = await this.$axios.delete(`api/agent/screenshots/${shot.id}`);

                const list = this.screenshotsData[userId];
                if (list) {
                    const idx = list.findIndex(s => s.id === shot.id);
                    if (idx !== -1) list.splice(idx, 1);
                }

                const meta = this.screenshotsMeta[userId];
                if (meta) {
                    this.$set(this.screenshotsMeta, userId, {
                        total: Math.max(0, meta.total - 1),
                        lowActivity: this.isLowActivity(shot) ? Math.max(0, meta.lowActivity - 1) : meta.lowActivity,
                    });
                }

                if (this.thumbnailUrls[shot.id]) {
                    this.$delete(this.thumbnailUrls, shot.id);
                }

                if (this.viewerShot?.id === shot.id) {
                    this.viewerOpen = false;
                }

                const secondsDeleted = res.data?.seconds_deleted ?? 0;
                if (secondsDeleted > 0) {
                    this.$toast.success(`Screenshot deleted. Removed ${this.formatDuration(secondsDeleted)} of tracked time.`);
                    // Aggregate totals (top apps, daily breakdown, averages) came
                    // from the server — reload them to reflect the reduction.
                    this.fetchData();
                } else {
                    this.$toast.success('Screenshot deleted');
                }
            } catch (e) {
                // The $axios plugin's delete() rejects with the raw error response
                // (not wrapped in `.response` like a normal axios catch).
                this.$toast.error(e?.data?.message || 'Failed to delete screenshot');
            } finally {
                this.isDeleting = false;
                this.confirmDeleteShot = null;
                this.confirmDeleteUserId = null;
                this.deletionPreview = null;
            }
        },

        // ---- Formatting -----------------------------------------------------

        formatDuration(seconds) {
            if (!seconds) return '0m';
            const h = Math.floor(seconds / 3600);
            const m = Math.floor((seconds % 3600) / 60);
            if (h > 0) return `${h}h ${m}m`;
            return `${m}m`;
        },

        formatDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr + 'T00:00:00');
            return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', weekday: 'short' });
        },

        // Rendered in the selected `timezone` (not the browser's own zone) so
        // the displayed time follows the timezone picker, and in 12-hour
        // AM/PM form to match the rest of the dashboard.
        formatShotTime(iso) {
            if (!iso) return '';
            return this.$date(iso).tz(this.timezone).format('D MMM h:mm A');
        },

        formatShotDateTime(iso) {
            if (!iso) return '';
            return this.$date(iso).tz(this.timezone).format('D MMM YYYY, h:mm A');
        },

        // YYYY-MM-DD in the selected `timezone` for grouping screenshots by
        // day — matches the calendar date shown in formatShotTime/formatShotDateTime.
        localDateKey(iso) {
            return this.$date(iso).tz(this.timezone).format('YYYY-MM-DD');
        },

        dayBarPct(seconds, user) {
            const maxDay = Math.max(...user.daily.map(d => d.seconds), 1);
            return Math.round((seconds / maxDay) * 100);
        },

        // Utilization = active (non-idle) time as a proportion of total time tracked that day.
        dayUtilizationPct(day) {
            if (!day.seconds) return 0;
            const active = Math.max(0, day.seconds - day.idle_seconds);
            return Math.round((active / day.seconds) * 100);
        },

        // Color-code utilization: <50% poor (red), 50-75% fair (amber), 75%+ good (green).
        utilizationColor(pct) {
            if (pct < 50) return 'red';
            if (pct < 75) return 'amber darken-2';
            return 'green';
        },

        // Vuetify text-color class equivalent of utilizationColor (handles the
        // "amber darken-2" -> "amber--text text--darken-2" split).
        utilizationTextClass(pct) {
            if (pct < 50) return 'red--text';
            if (pct < 75) return 'amber--text text--darken-2';
            return 'green--text';
        },

        // Scales a per-minute rate into a 0-100 bar percentage. The "max" values
        // are just visual reference points (not hard limits) for what counts as
        // a "full" activity bar — 60 keystrokes or clicks/min, 120 mouse moves/min.
        activityBarPct(value, max) {
            if (value === null || value === undefined) return 0;
            return Math.min(100, Math.round((value / max) * 100));
        },

        keystrokesPerMin(user) {
            const mins = user.total_seconds / 60;
            if (!mins) return '0';
            return Math.round(user.keystrokes_count / mins).toLocaleString();
        },

        mouseMovesPerMin(user) {
            const mins = user.total_seconds / 60;
            if (!mins) return '0';
            return Math.round(user.mouse_events_count / mins).toLocaleString();
        },

        mouseClicksPerMin(user) {
            const mins = user.total_seconds / 60;
            if (!mins) return '0';
            return Math.round(user.mouse_clicks_count / mins).toLocaleString();
        },

        cleanAppName(name) {
            return name.replace(/\.exe$/i, '');
        },

        faviconUrl(domain) {
            return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=32`;
        },

        onFaviconError(e) {
            e.target.style.visibility = 'hidden';
        },

        // Mirrors UserDirectoryComponent's getProfilePictureUrl — profile_picture
        // is stored as "/storage/..." already, so this must not re-prefix it.
        avatarUrl(path) {
            if (!path) return null;

            // If it's already a full URL, return as is
            if (path.startsWith('http://') || path.startsWith('https://')) {
                return path;
            }

            // Convert old /media/ format to new /storage/ format
            let picturePath = path;
            if (picturePath.startsWith('/media/')) {
                picturePath = picturePath.replace('/media/', '/storage/');
            }

            // Otherwise, construct the full URL with the API base URL
            const base = process.env.VUE_APP_API_URL || '';
            const apiUrl = base.endsWith('/') ? base.slice(0, -1) : base;
            picturePath = picturePath.startsWith('/') ? picturePath : `/${picturePath}`;
            return `${apiUrl}${picturePath}`;
        },
    },
};
</script>

<style scoped>
.screenshot-thumb-img {
    transition: border-color 0.15s;
}
.screenshot-thumb:hover .screenshot-thumb-img {
    border-color: var(--v-primary-base) !important;
}
</style>
